import {useMapContext} from "@luciad/ria-sample-common/hooks/useMapContext";
import {useToastContext} from "@luciad/ria-sample-common/hooks/useToast";
import {WMSCapabilities} from "@luciad/ria/model/capabilities/WMSCapabilities";
import {WMSCapabilitiesLayer} from "@luciad/ria/model/capabilities/WMSCapabilitiesLayer";
import {getReference} from "@luciad/ria/reference/ReferenceProvider";
import {ReferenceType} from "@luciad/ria/reference/ReferenceType";
import React, {ChangeEvent, useState} from "react";
import {addLayer} from "../DataFormatsUtil";
import {useWizard} from "../hooks/useWizard";
import {WMSDataLoader} from "../loaders/WMSDataLoader";
import {ConnectToURLPage} from "./ConnectToURLPage";
import {WizardPreviousNextButtons} from "./WizardPreviousNextButtons";

const webMercatorReference = getReference("EPSG:3857");

interface Props {
  onPrevious: () => void;
  onReset: () => void;
}

type WMSWizardPageKey = "enterUrl" | "loading" | "selectLayers";

export const ConnectToWMSWizard = ({onPrevious, onReset}: Props) => {
  const {currentPageKey, previousPageKey, changePage, resetWizard} = useWizard<WMSWizardPageKey>("enterUrl", onReset);
  const [capabilities, setCapabilities] = useState<WMSCapabilities | null>(null);

  const {map} = useMapContext();
  const {showErrorToast} = useToastContext();

  const handleURLSubmit = (url: string) => {
    changePage("loading");
    WMSCapabilities.fromURL(url).then(capabilities => {
      setCapabilities(capabilities);
      changePage("selectLayers");
    }).catch(error => {
      changePage("enterUrl");
      showErrorToast("Could not connect with the WMS server", error);
    });
  }

  const handleWMSLayerSelect = (wmsLayers: SelectedWMSLayer[]) => {
    if (capabilities) {
      try {
        const mapRefType = map.reference.referenceType;
        const preferredWMSModelReference =
            (mapRefType === ReferenceType.GRID || mapRefType === ReferenceType.CARTESIAN) ? map.reference
                                                                                          : webMercatorReference;
        const layer = WMSDataLoader.createLayer(capabilities, wmsLayers, preferredWMSModelReference, true);
        addLayer(map, layer).catch(error => {
          showErrorToast("Could not add WMS layer", error);
        });
      } catch(error) {
        console.log("Could not create WMS layer", error);
        // showErrorToast("Could not create WMS layer", error);
      } finally {
        resetWizard();
      }
    }
  }

  switch (currentPageKey) {
  case "enterUrl": {
    return <ConnectToURLPage defaultValue="https://sampleservices.luciad.com/wms"
                             onSubmit={handleURLSubmit}
                             onPrevious={onPrevious}
    />;
  }
  case "loading": {
    return <h1>Loading WMS capabilities...</h1>;
  }
  case "selectLayers": {
    return <SelectWMSLayers capabilities={capabilities!}
                            onSelect={handleWMSLayerSelect}
                            onPrevious={() => changePage(previousPageKey)}/>
  }
  }
}

const flattenWMSLayers = (layers: WMSCapabilitiesLayer[]): WMSCapabilitiesLayer[] => {
  let result: WMSCapabilitiesLayer[] = [];
  for (const layer of layers) {
    if (layer.name) {
      result.push(layer);
    }
    result = result.concat(layer.children);
  }
  return result;
}

interface SelectedWMSLayer {
  layer: WMSCapabilitiesLayer;
  style: string;
}

interface SelectWMSLayersProps {
  capabilities: WMSCapabilities;
  onSelect: (layers: SelectedWMSLayer[]) => void;
  onPrevious: () => void;
}

interface WMSLayerItem {
  layer: WMSCapabilitiesLayer;
  selected: boolean;
  selectedStyle: string;
}

const SelectWMSLayers = ({capabilities, onSelect, onPrevious}: SelectWMSLayersProps) => {
  const layers = flattenWMSLayers(capabilities.layers);
  const [items, setItems] = useState<WMSLayerItem[]>(layers.map(layer => {
    return {
      layer,
      selected: false,
      selectedStyle: layer.styles[0]?.name ?? "Default style"
    }
  }));

  const handleLayerSelectedChange = (ev: ChangeEvent<HTMLInputElement>, item: WMSLayerItem) => {
    const selected = ev.target.checked;
    setItems(items => items.map(i => {
      if (i === item) {
        return {...i, selected};
      }
      return i;
    }));
  }

  const handleStyleSelectionChange = (ev: ChangeEvent<HTMLSelectElement>, item: WMSLayerItem) => {
    const selectedStyle = ev.target.value;
    setItems(items => items.map(i => {
      if (i === item) {
        return {...i, selectedStyle};
      }
      return i;
    }));
  }

  const handleSelect = () => {
    const selectedItems = items.filter(i => i.selected).map(i => {
      return {
        layer: i.layer,
        style: i.selectedStyle
      }});
    onSelect(selectedItems);
  }

  return (
      <>
        <div>
          {items.map((item) => {
            const {name, title, styles} = item.layer;
            const showStylesSelect = styles.length >= 2;

            return (
                <div key={name}>
                  <input type="checkbox" onChange={(ev) => handleLayerSelectedChange(ev, item)}/>
                  {title}
                  {showStylesSelect && <select value={item.selectedStyle} onChange={ev => handleStyleSelectionChange(ev, item)}>
                    {styles.map(style => <option value={style.name}>{style.name}</option>)}
                  </select>}
                </div>)
          })}
        </div>
        <WizardPreviousNextButtons onPrevious={onPrevious} onNext={handleSelect}/>
      </>
  )

}
